package com.eladfinish.bible.contest.screens.completeVerse.ui

import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.ColorConstants
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.move_cursor_left
import com.eladfinish.bible.contest.move_cursor_right
import com.eladfinish.bible.contest.navigation.CompleteVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.platform.haptics.HapticService
import com.eladfinish.bible.contest.platform.shake.ShakeController
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseAction
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseEffect
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseStore
import com.eladfinish.bible.contest.screens.completeVerse.menu.CompleteVerseToolbarId
import com.eladfinish.bible.contest.screens.completeVerse.menu.ThreeDotsOption
import com.eladfinish.bible.contest.screens.completeVerse.menu.UnderlineOption
import com.eladfinish.bible.contest.screens.completeVerse.model.CompleteMode
import com.eladfinish.bible.contest.screens.completeVerse.model.VersePart
import com.eladfinish.bible.contest.screens.completeVerse.model.VerseWithBlanks
import com.eladfinish.bible.contest.screens.completeVerse.ui.dialogs.CompleteVerseDialogs
import com.eladfinish.bible.contest.screens.search.model.ClickType
import com.eladfinish.bible.contest.screens.search.model.HorizontalDirection
import com.eladfinish.bible.contest.ui.components.ShakeBindings
import com.eladfinish.bible.contest.ui.components.toolbar.UpdateToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.SubMenuContent
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.layout.isKeyboardCoveringShortWindow
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import com.eladfinish.bible.contest.ui.menus.actions.CompleteVerseToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import com.eladfinish.bible.contest.ui.scaffold.ShowSnackbar
import com.eladfinish.bible.contest.utils.addGershaimAndColon
import com.eladfinish.bible.contest.utils.setNoBreakSpaceBeforePasek
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.yield
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

private const val CORRECT_FLASH_DELAY_MS = 180L

// How long to keep pinning the cursor after the reveal button is released, covering
// the delayed input-session re-sync iOS fires when the reveal snackbar is dismissed.
private const val REVEAL_SELECTION_GUARD_SETTLE_MS = 150L

@Composable
fun CompleteVerseScreen(
    toolbarActionBus: ToolbarActionBusViewModel,
    showToast: (String) -> Unit,
    showToastRes: (StringResource) -> Unit,
    showSnackbar: ShowSnackbar,
    dismissSnackbar: () -> Boolean,
    openChapter: (bookIndex0: Int, chapterIndex: Int) -> Unit,
    store: CompleteVerseStore = koinViewModel(),
    hapticService: HapticService = getKoinInstance(),
    shakeController: ShakeController = getKoinInstance(),
) {
    val state by store.observeState().collectAsState()
    var textFieldValue by rememberSaveable(stateSaver = TextFieldValue.Saver) { mutableStateOf(TextFieldValue("")) }
    val focusRequester = remember { FocusRequester() }
    var isInputCorrect by rememberSaveable { mutableStateOf(false) }
    val inputColor = if (isInputCorrect) ColorConstants.GREEN else ColorConstants.RED
    // When non-null, the reveal ("רמז") button is being held. On iOS, showing /
    // dismissing the reveal snackbar re-syncs the text input session, which reports
    // the cursor jumped to the start of the text (TextRange(0)) even though nothing
    // was typed. We snapshot the real cursor here and keep it pinned while guarded.
    var revealSelectionGuard by remember { mutableStateOf<TextRange?>(null) }

    LaunchedEffect(Unit) {
        // Only the first time: a rotation, or returning from Browse (opened via the chapter dialog's
        // title), keeps the verse on screen and what was typed for it.
        if (state.verseWithBlanks.searchResult.id.isEmpty()) store.dispatch(CompleteVerseAction.ShowNewVerse)
    }

    // Keep guarding briefly after release: dismissing the snackbar triggers one more
    // input-session re-sync on iOS a moment later. Clear once it has settled.
    LaunchedEffect(state.isFullVerseVisible) {
        if (!state.isFullVerseVisible && revealSelectionGuard != null) {
            delay(REVEAL_SELECTION_GUARD_SETTLE_MS.milliseconds)
            revealSelectionGuard = null
        }
    }

    // Request focus when input becomes enabled (e.g. after NextVerseButton).
    // ResetUI effect fires BEFORE the state update, so we watch state directly.
    // In a short window the keyboard would hide the verse before it was seen: it opens on a tap,
    // and stays open from then on.
    val canOpenKeyboard by rememberUpdatedState(!isShortWideWindow() || isKeyboardCoveringShortWindow())
    // Saved: a rotation must not bring back a keyboard the user has closed.
    var didRequestFocus by rememberSaveable { mutableStateOf(false) }
    LaunchedEffect(state.isEditTextEnabled) {
        if (state.isEditTextEnabled && canOpenKeyboard && !didRequestFocus) focusRequester.requestFocus()
        didRequestFocus = state.isEditTextEnabled
    }

    HandleFullVerseSnackbar(
        isVisible = state.isFullVerseVisible,
        verseWithBlanks = state.verseWithBlanks,
        showSnackbar = showSnackbar,
        dismissSnackbar = dismissSnackbar,
    )

    ShakeBindings(
        isShakeEnabled = state.isShakeEnabled,
        shakeController = shakeController,
        onShaken = {
            hapticService.vibrateIfAllowed(milliseconds = 20)
            store.dispatch(CompleteVerseAction.ShowNewVerse)
        },
    )

    CompleteVerseToolbarBindings(
        toolbarActionBus = toolbarActionBus,
        onHelp = { store.dispatch(CompleteVerseAction.HelpClicked(input = textFieldValue.text)) },
        onNewVerse = { store.dispatch(CompleteVerseAction.ShowNewVerse) },
        onToolbarAction = { action -> store.dispatch(CompleteVerseAction.ToolbarClicked(action)) },
    )

    CompleteVerseMenuSync(completeMode = state.completeMode, versePart = state.versePart)

    CompleteVerseSideEffectsCollector(
        effects = store.observeSideEffect(),
        onResetUI = {
            textFieldValue = TextFieldValue("")
            isInputCorrect = false
            if (canOpenKeyboard) focusRequester.requestFocus()
        },
        onInputRequestFocus = { focusRequester.requestFocus() },
        onMoveCursor = { direction ->
            val current = textFieldValue.selection.start
            val newPos = when (direction) {
                HorizontalDirection.LEFT -> (current + 1).coerceAtMost(textFieldValue.text.length)
                HorizontalDirection.RIGHT -> (current - 1).coerceAtLeast(0)
            }
            textFieldValue = textFieldValue.copy(selection = TextRange(newPos))
        },
        onMoveCursorInfo = { direction ->
            val res = if (direction == HorizontalDirection.LEFT) SharedRes.strings.move_cursor_left
            else SharedRes.strings.move_cursor_right
            showToastRes(res)
        },
        onOnCorrect = { allWordsCompleted, _, _, _ ->
            if (!allWordsCompleted) hapticService.vibrateIfAllowed(milliseconds = 120)
            isInputCorrect = true
            delay(CORRECT_FLASH_DELAY_MS.milliseconds)
            textFieldValue = TextFieldValue("")
            isInputCorrect = false
        },
        onToastAndVibrate = { message, duration ->
            hapticService.vibrateIfAllowed(milliseconds = duration)
            showToastRes(message)
        },
        onNotifyError = { showToast(it) },
    )

    CompleteVerseContent(
        state = state,
        textFieldValue = textFieldValue,
        inputColor = inputColor,
        focusRequester = focusRequester,
        onTextChanged = { newValue ->
            val guard = revealSelectionGuard
            textFieldValue = if (guard != null && newValue.text == textFieldValue.text) {
                // While the hint is held, iOS may re-sync the input session and report
                // the cursor jumped to the start. Keep it where the user left it.
                newValue.copy(selection = guard)
            } else {
                newValue
            }
        },
        onCheckCorrectness = { store.dispatch(CompleteVerseAction.CheckCorrectness(textFieldValue.text)) },
        onImeNext = { store.dispatch(CompleteVerseAction.KeyboardNextClicked) },
        onClear = {
            textFieldValue = TextFieldValue("")
            hapticService.vibrateIfAllowed(milliseconds = 10)
        },
        onRevealPress = {
            // Snapshot the cursor synchronously, before the snackbar can disturb it.
            revealSelectionGuard = textFieldValue.selection
            store.dispatch(CompleteVerseAction.SetFullVerseVisible(true))
        },
        onRevealRelease = { store.dispatch(CompleteVerseAction.SetFullVerseVisible(false)) },
        onNextVerse = { store.dispatch(CompleteVerseAction.ShowNewVerse) },
        onSourceClick = { store.dispatch(CompleteVerseAction.VerseSourceClicked) },
        onMoveLeft = { store.dispatch(CompleteVerseAction.HorizontalArrowClicked(HorizontalDirection.LEFT, ClickType.TAP)) },
        onMoveRight = { store.dispatch(CompleteVerseAction.HorizontalArrowClicked(HorizontalDirection.RIGHT, ClickType.TAP)) },
        onMoveLeftLongPress = { store.dispatch(CompleteVerseAction.HorizontalArrowClicked(HorizontalDirection.LEFT, ClickType.PRESS)) },
        onMoveRightLongPress = { store.dispatch(CompleteVerseAction.HorizontalArrowClicked(HorizontalDirection.RIGHT, ClickType.PRESS)) },
    )

    CompleteVerseDialogs(
        dialogState = state.dialogState,
        onAction = store::dispatch,
        onNumberPicked = {
            focusRequester.requestFocus()
            store.dispatch(CompleteVerseAction.ShowNewVerse)
        },
        onChapterDialogTitleClick = openChapter,
    )
}

// ── Snackbar ──────────────────────────────────────────────────────────────────

@Composable
private fun HandleFullVerseSnackbar(
    isVisible: Boolean,
    verseWithBlanks: VerseWithBlanks,
    showSnackbar: ShowSnackbar,
    dismissSnackbar: () -> Boolean,
) {
    val currentShowSnackbar by rememberUpdatedState(showSnackbar)
    val currentDismissSnackbar by rememberUpdatedState(dismissSnackbar)

    LaunchedEffect(isVisible) {
        if (isVisible) {
            val verseText = verseWithBlanks.searchResult.let {
                it.text.setNoBreakSpaceBeforePasek().addGershaimAndColon() + " " + it.source
            }
            currentShowSnackbar(verseText.desc(), null, SnackbarDuration.Indefinite) {}
        } else {
            // yield() lets Main dispatcher run the showSnackbar coroutine so that
            // currentSnackbarData is set before we dismiss.
            yield()
            currentDismissSnackbar()
        }
    }
}

// ── Toolbar bindings ──────────────────────────────────────────────────────────

@Composable
private fun CompleteVerseToolbarBindings(
    toolbarActionBus: ToolbarActionBusViewModel,
    onHelp: () -> Unit,
    onNewVerse: () -> Unit,
    onToolbarAction: (CompleteVerseToolbarAction) -> Unit,
) {
    val screenKey = CompleteVerseScreenNavigationDestination::class.screenKey
    val currentOnHelp by rememberUpdatedState(onHelp)
    val currentOnNewVerse by rememberUpdatedState(onNewVerse)
    val currentOnToolbarAction by rememberUpdatedState(onToolbarAction)

    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            when (val action = event.action as? CompleteVerseToolbarAction ?: return@collect) {
                CompleteVerseToolbarAction.Help -> currentOnHelp()
                CompleteVerseToolbarAction.NewVerse -> currentOnNewVerse()
                is CompleteVerseToolbarAction.ThreeDotsMode -> currentOnToolbarAction(action)
                is CompleteVerseToolbarAction.UnderlineMode -> currentOnToolbarAction(action)
            }
        }
    }
}

// ── Toolbar menu sync ─────────────────────────────────────────────────────────

@Composable
private fun CompleteVerseMenuSync(
    completeMode: CompleteMode,
    versePart: VersePart,
) {
    val underlineSelected = when {
        completeMode == CompleteMode.HIDE_SOME_WORDS -> UnderlineOption.HIDE_SOME_WORDS
        versePart == VersePart.BEFORE_ATNACHTA -> UnderlineOption.SHOW_BEGINNING
        else -> UnderlineOption.SHOW_END
    }
    val threeDotsSelected = when (versePart) {
        VersePart.BEFORE_ATNACHTA -> ThreeDotsOption.SHOW_BEGINNING
        VersePart.AFTER_ATNACHTA -> ThreeDotsOption.SHOW_END
    }

    UpdateToolbarConfiguration<CompleteVerseToolbarId>(
        update = {
            copy(
                actions = actions.map { action ->
                    val subMenu = action as? ToolbarActionConfig.SubMenu<CompleteVerseToolbarId>
                        ?: return@map action
                    when (subMenu.id) {
                        CompleteVerseToolbarId.UNDERLINE_MODE -> {
                            @Suppress("UNCHECKED_CAST")
                            val radio = subMenu.content
                                    as? SubMenuContent.RadioSubMenu<CompleteVerseToolbarId, UnderlineOption>
                                ?: return@map action
                            subMenu.copy(content = radio.copy(selected = underlineSelected))
                        }

                        CompleteVerseToolbarId.THREE_DOTS_MODE -> {
                            @Suppress("UNCHECKED_CAST")
                            val radio = subMenu.content
                                    as? SubMenuContent.RadioSubMenu<CompleteVerseToolbarId, ThreeDotsOption>
                                ?: return@map action
                            subMenu.copy(content = radio.copy(selected = threeDotsSelected))
                        }

                        else -> action
                    }
                }.toImmutableList()
            )
        },
        completeMode,
        versePart,
    )
}

// ── Side-effects collector ────────────────────────────────────────────────────

@Composable
private fun CompleteVerseSideEffectsCollector(
    effects: Flow<CompleteVerseEffect>,
    onResetUI: suspend () -> Unit,
    onInputRequestFocus: () -> Unit,
    onMoveCursor: (HorizontalDirection) -> Unit,
    onMoveCursorInfo: (HorizontalDirection) -> Unit,
    onOnCorrect: suspend (allWordsCompleted: Boolean, text: String, completeMode: CompleteMode, verseWithBlanks: VerseWithBlanks) -> Unit,
    onToastAndVibrate: (message: StringResource, duration: Long) -> Unit,
    onNotifyError: (String) -> Unit,
) {
    val currentOnResetUI by rememberUpdatedState(onResetUI)
    val currentOnInputRequestFocus by rememberUpdatedState(onInputRequestFocus)
    val currentOnMoveCursor by rememberUpdatedState(onMoveCursor)
    val currentOnMoveCursorInfo by rememberUpdatedState(onMoveCursorInfo)
    val currentOnOnCorrect by rememberUpdatedState(onOnCorrect)
    val currentOnToastAndVibrate by rememberUpdatedState(onToastAndVibrate)
    val currentOnNotifyError by rememberUpdatedState(onNotifyError)

    LaunchedEffect(Unit) {
        effects.collect { effect ->
            when (effect) {
                CompleteVerseEffect.ResetUI -> currentOnResetUI()
                CompleteVerseEffect.InputRequestFocus -> currentOnInputRequestFocus()
                is CompleteVerseEffect.MoveCursor -> currentOnMoveCursor(effect.direction)
                is CompleteVerseEffect.MoveCursorInfo -> currentOnMoveCursorInfo(effect.direction)
                is CompleteVerseEffect.OnCorrect -> currentOnOnCorrect(
                    effect.allWordsCompleted, effect.text, effect.completeMode, effect.verseWithBlanks,
                )

                is CompleteVerseEffect.ToastAndVibrate -> currentOnToastAndVibrate(
                    effect.stringResource, effect.duration,
                )

                is CompleteVerseEffect.NotifyError -> currentOnNotifyError(effect.text)
                is CompleteVerseEffect.ShowPasukWithBlanks -> Unit
            }
        }
    }
}
